'use client'

import { EProductActiveWomenType } from '@/app/(landing-page)/huong-dan-chon-size-do-nu/_components/type'
import { ModalPicker } from '@/components/extension/modal-picker'
import { Button } from '@/components/ui/button'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { useCalculateSizeMutation } from '@/hooks/react-query/size/use-size'
import { LocalStorage } from '@/lib/utils/local-storage'
import { TCalculateSizeParams } from '@/types/size'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect, useState } from 'react'
import { FieldName, useForm } from 'react-hook-form'
import { z } from 'zod'
import { SIZE_CONFIG } from '../../../../../lib/constants/size'
import { useProductDetailSafe } from '../../context/product-detail-context'

type Props = {
  sizeChart: string
}

interface StoredSizeData {
  [key: string]: number | string
  type: string
}

export const CalculateSize = ({ sizeChart }: Props) => {
  const sizeConfig = SIZE_CONFIG[sizeChart]

  console.log('sizeConfig', sizeConfig, 'sizeChart', sizeChart)
  const [calculatedSize, setCalculatedSize] = useState<string | null>(null)
  const [openModalPickerXAxis, setOpenModalPickerXAxis] = useState(false)
  const [openModalPickerYAxis, setOpenModalPickerYAxis] = useState(false)
  const { setSizeSelected } = useProductDetailSafe()
  const [hasSubmitted, setHasSubmitted] = useState(false)

  const { mutateAsync: calculateSize, isPending } = useCalculateSizeMutation()

  // Tạo schema động dựa trên cấu hình size
  const FormSchema = z.object({
    [sizeConfig.xAxis]: z
      .number({
        required_error: 'Vui lòng nhập giá trị',
        invalid_type_error: 'Vui lòng nhập số',
      })
      .min(sizeConfig.minXAxis, `Giá trị tối thiểu là ${sizeConfig.minXAxis}`)
      .max(sizeConfig.maxXAxis, `Giá trị tối đa là ${sizeConfig.maxXAxis}`),
    [sizeConfig.yAxis]: z
      .number({
        required_error: 'Vui lòng nhập giá trị',
        invalid_type_error: 'Vui lòng nhập số',
      })
      .min(sizeConfig.minYAxis, `Giá trị tối thiểu là ${sizeConfig.minYAxis}`)
      .max(sizeConfig.maxYAxis, `Giá trị tối đa là ${sizeConfig.maxYAxis}`),
  })

  type FormValues = z.infer<typeof FormSchema>

  // Get saved values from localStorage
  const savedData = LocalStorage.get<StoredSizeData>('FEMALE_SIZE_DATA')

  // Extract only the numeric values for form initialization
  const initialData = savedData
    ? {
        [sizeConfig.xAxis]: Number(savedData[sizeConfig.xAxis]),
        [sizeConfig.yAxis]: Number(savedData[sizeConfig.yAxis]),
      }
    : null

  const form = useForm<FormValues>({
    resolver: zodResolver(FormSchema),
    mode: 'onChange',
    defaultValues: {
      [sizeConfig.xAxis]:
        initialData?.[sizeConfig.xAxis] ?? sizeConfig.minXAxis,
      [sizeConfig.yAxis]:
        initialData?.[sizeConfig.yAxis] ?? sizeConfig.minYAxis,
    },
  })

  const onSubmit = async (values: FormValues) => {
    const xKey = sizeConfig.xAxis as keyof FormValues
    const yKey = sizeConfig.yAxis as keyof FormValues

    const requestData: TCalculateSizeParams = {
      type: sizeChart as EProductActiveWomenType,
      [xKey]: values[xKey],
      [yKey]: values[yKey],
    }
    try {
      const res = await calculateSize(requestData)
      setCalculatedSize(res?.data?.size || '')
      setHasSubmitted(true)
      if (setSizeSelected) {
        setSizeSelected(res?.data?.size || '')
      }
    } catch (error) {
      console.log('🧙 ~ error:', error)
      setCalculatedSize('')
      setHasSubmitted(true)
    }
  }

  const xAxisValue = form.watch(sizeConfig.xAxis as FieldName<FormValues>)
  const yAxisValue = form.watch(sizeConfig.yAxis as FieldName<FormValues>)

  // Save to localStorage whenever values change
  useEffect(() => {
    if (xAxisValue && yAxisValue) {
      const dataToStore: StoredSizeData = {
        [sizeConfig.xAxis]: Number(xAxisValue),
        [sizeConfig.yAxis]: Number(yAxisValue),
        type: sizeChart,
      }
      LocalStorage.set('FEMALE_SIZE_DATA', dataToStore)
    }
  }, [xAxisValue, yAxisValue, sizeConfig.xAxis, sizeConfig.yAxis, sizeChart])

  return (
    <div>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-3'>
          <div className='flex items-center gap-6'>
            <FormField
              control={form.control}
              name={sizeConfig.xAxis as FieldName<FormValues>}
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{sizeConfig.xAxisLabel}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        readOnly
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder='Nhập giá trị'
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            cm
                          </p>
                        }
                        onClick={() => {
                          setOpenModalPickerXAxis(true)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name={sizeConfig.yAxis as FieldName<FormValues>}
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>{sizeConfig.yAxisLabel}</FormLabel>
                  <FormControl>
                    <div className='relative w-full'>
                      <Input
                        {...field}
                        readOnly
                        className='rounded-lg border-neutral-300 px-4 py-3'
                        placeholder='Nhập giá trị'
                        suffix={
                          <p className='font-sans text-sm font-normal leading-5 text-neutral-300'>
                            cm
                          </p>
                        }
                        onClick={() => {
                          setOpenModalPickerYAxis(true)
                        }}
                      />
                    </div>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button
            variant='black'
            type='submit'
            className='mt-3'
            disabled={isPending}
          >
            {isPending ? 'Đang tính toán...' : 'Tính toán'}
          </Button>
        </form>
      </Form>

      {hasSubmitted &&
        (calculatedSize && calculatedSize !== 'Không tìm thấy' ? (
          <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
            Size phù hợp của bạn là{' '}
            <span className='font-bold'>{calculatedSize}</span> – Hoàn hảo cho
            sự thoải mái và vừa vặn!
          </p>
        ) : (
          <p className='mt-3 rounded bg-secondary-50 px-4 py-3 text-base leading-5 text-neutral-800 opacity-90'>
            Rất tiếc, Coolmate chưa có size gợi ý chính xác cho bạn. Vui lòng
            tham khảo bảng size bên dưới để chọn sản phẩm phù hợp nhất!
          </p>
        ))}

      {openModalPickerXAxis && (
        <div className='z-[100]'>
          <ModalPicker
            onClose={() => {
              setOpenModalPickerXAxis(false)
            }}
            title={`Chọn ${sizeConfig.xAxisLabel}`}
            pickers={[
              {
                title: sizeConfig.xAxisLabel,
                key: sizeConfig.xAxis,
                options: Array.from(
                  { length: sizeConfig.maxXAxis - sizeConfig.minXAxis + 1 },
                  (_, i) => {
                    const value = sizeConfig.minXAxis + i
                    return {
                      label: `${value}`,
                      value: `${value}`,
                    }
                  }
                ),
                selected: xAxisValue?.toString(),
                className: 'h-36',
              },
            ]}
            onConfirm={(selected) => {
              const value = selected[sizeConfig.xAxis] ?? sizeConfig.minXAxis
              form.setValue(sizeConfig.xAxis as FieldName<FormValues>, value)
              setOpenModalPickerXAxis(false)
            }}
          />
        </div>
      )}

      {openModalPickerYAxis && (
        <ModalPicker
          onClose={() => {
            setOpenModalPickerYAxis(false)
          }}
          title={`Chọn ${sizeConfig.yAxisLabel}`}
          pickers={[
            {
              title: sizeConfig.yAxisLabel,
              key: sizeConfig.yAxis,
              options: Array.from(
                { length: sizeConfig.maxYAxis - sizeConfig.minYAxis + 1 },
                (_, i) => {
                  const value = sizeConfig.minYAxis + i
                  return {
                    label: `${value}`,
                    value: `${value}`,
                  }
                }
              ),
              selected: yAxisValue?.toString(),
              className: 'h-36',
            },
          ]}
          onConfirm={(selected) => {
            const value = selected[sizeConfig.yAxis] ?? sizeConfig.minYAxis
            form.setValue(sizeConfig.yAxis as FieldName<FormValues>, value)
            setOpenModalPickerYAxis(false)
          }}
        />
      )}
    </div>
  )
}
